<!-- Runtime value prompt for design actions (set_variable "ask user"): a modal
     that asks the operator for a value — free text or an on-screen numeric
     keypad (SCADA-style, like iMonitor). Used by the editor's Run mode and the
     inline chat viewer. Imperative API: `open(opts) → Promise<string | null>`
     (null = cancelled). -->
<template>
  <transition name="vp-fade">
    <div v-if="open" class="vp-overlay" @pointerdown.self="cancel">
      <div class="vp-card" role="dialog" @keydown.enter.prevent="confirm" @keydown.esc.prevent="cancel">
        <p class="vp-label">{{ label || 'Enter a value' }}</p>
        <div class="vp-display-row">
          <input
            ref="textEl"
            class="vp-display"
            type="text"
            :inputmode="kind === 'number' ? 'decimal' : 'text'"
            v-model="entry"
            :placeholder="kind === 'number' ? '0' : '…'"
            @keydown.enter.prevent="confirm"
          />
          <span v-if="unit" class="vp-unit">{{ unit }}</span>
        </div>
        <p v-if="rangeHint" class="vp-range" :class="{ bad: !valid && entry !== '' }">{{ rangeHint }}</p>
        <div v-if="kind === 'number'" class="vp-pad">
          <button v-for="k in PAD" :key="k" type="button" class="vp-key" :class="{ wide: k === '0' }" @click="press(k)">{{ k }}</button>
          <button type="button" class="vp-key" @click="press('±')">±</button>
          <button type="button" class="vp-key" @click="backspace">⌫</button>
        </div>
        <div class="vp-actions">
          <button type="button" class="vp-btn ghost" @click="cancel">Cancel</button>
          <button type="button" class="vp-btn ok" :disabled="!valid" @click="confirm">OK</button>
        </div>
      </div>
    </div>
  </transition>
</template>

<script setup lang="ts">
import { computed, nextTick, ref } from 'vue';

const PAD = ['7', '8', '9', '4', '5', '6', '1', '2', '3', '.', '0'];

const open = ref(false);
const kind = ref<'text' | 'number'>('text');
const label = ref('');
const entry = ref('');
const unit = ref('');
const min = ref<number | undefined>(undefined);
const max = ref<number | undefined>(undefined);
const textEl = ref<HTMLInputElement | null>(null);
let resolver: ((v: string | null) => void) | null = null;

// A numeric entry is valid when it parses and sits within [min,max] (if set).
const valid = computed(() => {
  if (kind.value !== 'number') return entry.value.trim().length > 0;
  const n = Number(entry.value);
  if (entry.value.trim() === '' || !Number.isFinite(n)) return false;
  if (min.value != null && n < min.value) return false;
  if (max.value != null && n > max.value) return false;
  return true;
});
const rangeHint = computed(() => {
  if (kind.value !== 'number' || (min.value == null && max.value == null)) return '';
  const u = unit.value ? ' ' + unit.value : '';
  if (min.value != null && max.value != null) return `Allowed: ${min.value}–${max.value}${u}`;
  if (min.value != null) return `Min: ${min.value}${u}`;
  return `Max: ${max.value}${u}`;
});

function open_(opts: { kind?: 'text' | 'number'; label?: string; initial?: string; unit?: string; min?: number; max?: number } = {}): Promise<string | null> {
  kind.value = opts.kind === 'number' ? 'number' : 'text';
  label.value = opts.label ?? '';
  entry.value = opts.initial ?? '';
  unit.value = opts.unit ?? '';
  min.value = typeof opts.min === 'number' && Number.isFinite(opts.min) ? opts.min : undefined;
  max.value = typeof opts.max === 'number' && Number.isFinite(opts.max) ? opts.max : undefined;
  open.value = true;
  void nextTick(() => textEl.value?.focus());
  return new Promise<string | null>((res) => { resolver = res; });
}

function press(k: string): void {
  if (k === '±') { entry.value = entry.value.startsWith('-') ? entry.value.slice(1) : '-' + entry.value; return; }
  if (k === '.' && entry.value.includes('.')) return;
  entry.value += k;
}
function backspace(): void { entry.value = entry.value.slice(0, -1); }
function settle(v: string | null): void {
  open.value = false;
  const r = resolver; resolver = null;
  r?.(v);
}
function confirm(): void { if (!valid.value) return; settle(entry.value); }
function cancel(): void { settle(null); }

defineExpose({ open: open_ });
</script>

<style scoped>
.vp-overlay { position: fixed; inset: 0; z-index: 80; display: flex; align-items: center; justify-content: center; background: rgba(0,0,0,.45); }
.vp-card { width: 280px; max-width: 90vw; background: var(--color-surface); border: 1px solid var(--color-line); border-radius: 12px; box-shadow: 0 20px 60px rgba(0,0,0,.35); padding: 16px; }
.vp-label { margin: 0 0 10px; font-size: .85rem; font-weight: 600; color: var(--color-ink); }
.vp-display-row { display: flex; align-items: center; gap: 8px; }
.vp-display { flex: 1; min-width: 0; box-sizing: border-box; border: 1px solid var(--color-line-strong); border-radius: 8px; padding: 10px 12px; font-size: 1.25rem; text-align: right; color: var(--color-ink); background: var(--color-surface-2); font-variant-numeric: tabular-nums; }
.vp-unit { font-size: 1rem; color: var(--color-muted); white-space: nowrap; }
.vp-range { margin: 6px 0 0; font-size: .72rem; color: var(--color-muted); }
.vp-range.bad { color: var(--color-danger); }
.vp-btn.ok:disabled { opacity: .5; cursor: not-allowed; }
.vp-pad { display: grid; grid-template-columns: repeat(3, 1fr); gap: 8px; margin-top: 12px; }
.vp-key { padding: 14px 0; font-size: 1.1rem; border: 1px solid var(--color-line); border-radius: 8px; background: var(--color-surface); color: var(--color-ink); cursor: pointer; }
.vp-key:hover { background: var(--color-surface-2); }
.vp-key:active { background: var(--color-line); }
.vp-actions { display: flex; gap: 8px; margin-top: 14px; }
.vp-btn { flex: 1; padding: 9px 0; border-radius: 8px; font-size: .85rem; font-weight: 600; cursor: pointer; border: 1px solid var(--color-line-strong); }
.vp-btn.ghost { background: var(--color-surface); color: var(--color-muted); }
.vp-btn.ghost:hover { background: var(--color-surface-2); color: var(--color-ink); }
.vp-btn.ok { background: var(--color-brand-600); border-color: var(--color-brand-600); color: #fff; }
.vp-btn.ok:hover { background: var(--color-brand-700); }
.vp-fade-enter-active, .vp-fade-leave-active { transition: opacity .15s ease; }
.vp-fade-enter-from, .vp-fade-leave-to { opacity: 0; }
</style>
